import { useEffect, useState } from 'react'
import { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ContainerGroupField } from '@/components/common'
import {
    AutocompleteControl,
    InputControl,
    SelectControl
} from '@/components/form-controls'
import { useResourcesApi } from '@/services/api/resourcesApi'
import { JobInformationFormData } from '@/types/forms/jobInformation'

interface CompanyControlProps {
    control: Control<JobInformationFormData>
}

// Helper function to extract data from API response
// Handles both formats: direct array or ApiResponse object with data property
const extractDataFromResponse = (response: any): any[] => {
    // Si la respuesta es directamente un array (formato local)
    if (Array.isArray(response)) {
        return response
    }

    // Si la respuesta es un objeto ApiResponse con data (formato AWS)
    if (response && response.data && Array.isArray(response.data)) {
        return response.data
    }

    // Si no coincide con ningún formato conocido, retornar array vacío
    return []
}

// Helper function to map and filter options
const mapAndFilterOptions = (data: any[]): any[] => {
    const mapped = Array.isArray(data)
        ? data.map((item) => ({
              value: item.value,
              label: item.label
          }))
        : []

    return mapped.filter(
        (option) =>
            option.value !== '' &&
            option.label !== '' &&
            option.value !== null &&
            option.label !== null
    )
}

export const CompanyControl = ({
    control
}: CompanyControlProps): JSX.Element => {
    const { t } = useTranslation()
    const resourcesApi = useResourcesApi()
    const [hasFetched, setHasFetched] = useState<boolean>(false)
    const [optionsActivity, setOptionsActivity] = useState<any>([])
    const [optionsKindOfCompany, setOptionsKindOfCompany] = useState<any>([])
    const [optionsBusinessSector, setOptionsBusinessSector] = useState<any>([])
    const [optionsPositionLevel, setOptionsPositionLevel] = useState<any>([])

    useEffect(() => {
        let mounted = true

        if (hasFetched) {
            return
        }

        const fetchAllOptions = async (): Promise<void> => {
            const [
                responseProfesionalActivity,
                responseKindOfCompany,
                responseBusinessSector,
                responsePositionLevel
            ] = await Promise.all([
                resourcesApi.getEconomicActivity(),
                resourcesApi.getEnterpriseType(),
                resourcesApi.getBusinessSector(),
                resourcesApi.getPositionLevel()
            ])

            if (!mounted) {
                return
            }

            // Extraer los datos de cada respuesta antes de mapearlos
            // Esto maneja tanto el formato local (array directo) como AWS (objeto con data)
            const activityData = extractDataFromResponse(
                responseProfesionalActivity
            )
            const kindOfCompanyData = extractDataFromResponse(
                responseKindOfCompany
            )
            const businessSectorData = extractDataFromResponse(
                responseBusinessSector
            )
            const positionLevelData = extractDataFromResponse(
                responsePositionLevel
            )

            setOptionsActivity(mapAndFilterOptions(activityData))
            setOptionsKindOfCompany(mapAndFilterOptions(kindOfCompanyData))
            setOptionsBusinessSector(mapAndFilterOptions(businessSectorData))
            setOptionsPositionLevel(mapAndFilterOptions(positionLevelData))

            setHasFetched(true)
        }

        fetchAllOptions()

        return () => {
            mounted = false
        }
    }, [resourcesApi, hasFetched])

    return (
        <ContainerGroupField tittleGroup={t('forms.groups.company')}>
            <InputControl
                name="companyName"
                control={control}
                label={t('jobInformation.companyName')}
                rules={{
                    required: t('jobInformation.required.companyNameRequired')
                }}
                allowedPattern={/^[a-zA-Z0-9 ]*$/}
            />
            <SelectControl
                name="activity"
                control={control}
                label={t('jobInformation.activity')}
                options={optionsActivity}
                rules={{
                    required: t('jobInformation.required.activityRequired')
                }}
            />
            <SelectControl
                name="kindOfCompany"
                control={control}
                label={t('jobInformation.kindOfCompany')}
                options={optionsKindOfCompany}
                rules={{
                    required: t('jobInformation.required.kindOfCompanyRequired')
                }}
            />

            <AutocompleteControl
                name="businessSector"
                control={control}
                label={t('jobInformation.businessSector')}
                options={optionsBusinessSector}
                rules={{
                    required: t(
                        'jobInformation.required.businessSectorRequired'
                    )
                }}
            />

            <SelectControl
                name="workName"
                control={control}
                label={t('jobInformation.workName')}
                options={optionsPositionLevel}
                rules={{
                    required: t('jobInformation.required.workNameRequired')
                }}
            />
        </ContainerGroupField>
    )
}
